import {
  Component,
  Host,
  Prop,
  Element,
  State,
  getAssetPath,
  Method,
  h,
  Watch
} from "@stencil/core";

import { MercuryBundles, getIconPath } from "@genexus/mercury";

import { config } from "../../common/config";
import { Locale } from "../../common/locale";
import { getSelectedItem, setSelectedItem } from "../../common/common";
import {
  mapOptionsToComboBoxItemModel,
  renderChCheckboxItems
} from "../../common/helpers";
import { FormSubmitResult, GxOption } from "../../common/types";
import {
  ControlValidation,
  validateControls
} from "../../common/form-validation";
import {
  TabModel,
  ComboBoxModel,
  ChCheckboxCustomEvent
} from "@genexus/chameleon-controls-library";
import { RadioGroupItemModel } from "@genexus/chameleon-controls-library/dist/types/components/radio-group/types";
import { removeIndex } from "../../common/array";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/tab",
  "components/tooltip",
  "utils/form--full",
  "utils/layout",
  "utils/typography",
  "utils/spacing",
  "chameleon/scrollbar"
];

const WINDOWS_AUTHENTICATION_VALUE: string = "windows-authentication";
const MAIN_SECTION_CLASSES: string = "main";
const BASIC_SECTION_PARENT_CLASSES: string = "basic";

const SYSTEM_ICON = getIconPath({
  category: "system",
  name: "folder",
  colorType: "primary"
});


const LOCATION_MODE_DEFAULT = "default";
const LOCATION_MODE_CUSTOM = "custom";

const BASIC_TAB_SELECTOR: string = "basic";
const ADVANCED_TAB_SELECTOR: string = "advanced";

@Component({
  tag: "gx-ide-new-kb",
  styleUrl: "new-kb.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/new-kb"]
})
export class GxIdeNewKb {
  #advancedTabHasBeenRendered: boolean = false;
  /**
   * The component hard-coded strings translations.
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #componentLocale: any;
  /**
   * frontEndsState saves the id's of the front ends checkboxes that are checked
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #frontEndsCheckedMap = new Map<string, string[]>();
  #selectedPrototypingEnvironmentId: string;
  #selectedDataSourceId: string;
  #shortcutsSrc = getAssetPath(`./gx-ide-assets/new-kb/shortcuts.json`);
  #kbSectionsModel: TabModel = [];

  @Element() el: HTMLGxIdeNewKbElement;

  /* References needed to collect data con "Create" button submit*/
  #authenticationTypeEl!: HTMLChComboBoxRenderElement;
  #collationEl!: HTMLChComboBoxRenderElement;
  #createDataFilesInKBFolderEl!: HTMLChCheckboxElement;
  #databaseNameEl!: HTMLChEditElement;
  #dataSourceEl!: HTMLChComboBoxRenderElement;
  #kbNameEl!: HTMLChEditElement;
  #locationEl!: HTMLChEditElement;
  #locationModeEl!: HTMLChRadioGroupRenderElement;
  #locationModeModel: RadioGroupItemModel[];
  #locationElRegistered = false;
  #passwordEl!: HTMLChEditElement;
  #prototypingEnvironmentEl!: HTMLChComboBoxRenderElement;
  #prototypingTargetEl!: HTMLChComboBoxRenderElement;
  #savePasswordEl!: HTMLChCheckboxElement;
  #serverNameEl!: HTMLChComboBoxRenderElement;
  #userInterfaceLanguageEl!: HTMLChComboBoxRenderElement;
  #userNameEl!: HTMLChEditElement;

  #chShortcutsEl: HTMLChShortcutsElement;

  @State() advancedTabIsVisible: boolean = false;
  @State() locationMode: string = LOCATION_MODE_DEFAULT;
  /**
   * Allows selecting multiple generators for the front end
   * TODO: Check if this property can be deleted, since we can call getFrontEndsCallback.
   */
  @State() frontEnds: GxOption[];
  @State() validatableControls = new Map<string, ControlValidation>();
  @State() createDataInKbDisabled = false;
  @State() userNameVisible = true;
  @State() passwordVisible = true;
  @State() savePasswordVisible = true;
  // representations of GxOption[] as ComboBoxModel, and initialValue for each one.
  @State() authenticationTypesComboBoxModel: ComboBoxModel = [];
  @State() authenticationTypesInitialValue: string;
  @State() collationsComboBoxModel: ComboBoxModel = [];
  @State() collationsInitialValue: string;
  @State() defaultDataSourcesComboBoxModel: ComboBoxModel = [];
  @State() prototypingTargetsComboBoxModel: ComboBoxModel = [];
  @State() prototypingTargetsInitialValue: string;
  @State() prototypingEnvironmentsComboBoxModel: ComboBoxModel = [];
  @State() prototypingEnvironmentsInitialValue: string;
  @State() serverNamesComboBoxModel: ComboBoxModel = [];
  @State() serverNamesInitialValue: string;
  @State() userInterfaceLanguagesComboBoxModel: ComboBoxModel = [];
  @State() userInterfaceLanguagesInitialValue: string;

  /**
   * Defines the type of authentication for the connection to the previously defined database
   */
  @Prop() readonly authenticationTypes: GxOption[] = [];
  @Watch("authenticationTypes")
  watchAuthenticationTypes(newValue: GxOption[]) {
    if (newValue?.length) {
      this.authenticationTypesComboBoxModel =
        mapOptionsToComboBoxItemModel(newValue);
      this.authenticationTypesInitialValue = getSelectedItem(newValue);
    }
  }

  /**
   * Callback invoked when the user wants to cancel KB creation (‘Cancel’ button)
   */
  @Prop() readonly cancelCallback: CancelCallback;

  /**
   * Specifies whether the location field (and its folder-picker button) is
   * shown in the header, allowing the user to override the default `kbLocation`
   * path. Independent from `isAdvanced`.
   */
  @Prop() readonly canSetLocation: boolean = false;

  /**
   * DB Collations
   */
  @Prop() readonly collations!: GxOption[];
  @Watch("collations")
  watchCollations(newValue: GxOption[]) {
    if (newValue?.length) {
      this.collationsComboBoxModel = mapOptionsToComboBoxItemModel(newValue);
      this.collationsInitialValue = getSelectedItem(newValue);
    }
  }

  /**
   * Callback that will be invoked when the user confirms KB creation (‘Create’ button)
   */
  @Prop() readonly createCallback!: CreateCallback;

  /**
   * Disabled if the first item of the 'Server Name' combo is selected
   */
  @Prop() readonly createDataFilesInKBFolder: boolean = false;

  /**
   * Name of the database where we are going to persist the information of our KB
   */
  @Prop() readonly databaseName: string;

  /**
   * It allows defining the DBMS that will be used in the solution
   */
  @Prop({ mutable: true }) defaultDataSources: GxOption[] = [];
  @Watch("defaultDataSources")
  watchDefaultDataSources(newValue: GxOption[]) {
    if (newValue?.length) {
      this.defaultDataSourcesComboBoxModel =
        mapOptionsToComboBoxItemModel(newValue);
      const result = setSelectedItem(newValue, this.#selectedDataSourceId);
      this.#selectedDataSourceId = getSelectedItem(result);
    }
  }

  /**
   * Callback invoked when the user wants to cancel KB creation (‘Cancel’ button)
   */
  @Prop() readonly getDataSourcesCallback: GetDataSourcesCallback;

  /**
   * Callback invoked when the user changes the prototyping environment, allowing for the modification of the list of front ends
   */
  @Prop() readonly getFrontEndsCallback!: GetFrontEndsCallback;

  /**
   * Callback invoked when the user changes the prototyping target id, allowing for the modification of the list of prototyping environments
   */
  @Prop()
  readonly getPrototypingEnvironmentsCallback!: GetPrototypingEnvironmentsCallback;

  /**
   * Specify whether it is feasible to display information related to local configuration parameters
   */
  @Prop() readonly isAdvanced = false;

  /**
   * Default suggested path to a directory where the information related to the KB will be stored/generated
   */
  @Prop({ mutable: true }) kbLocation: string;

  /**
   * The knowledge base default suggested name
   */
  @Prop() readonly kbName: string;

  /**
   * Password for the database connection. Visible if the Authentication Type is different from Windows Authentication (first item of the combo)
   */
  @Prop() readonly password: string;

  /**
   * It allows selecting the default environment that the KB will have (additional environments can be created later)
   */
  @Prop({ mutable: true }) defaultPrototypingEnvironments: GxOption[] = [];
  @Watch("defaultPrototypingEnvironments")
  watchDefaultPrototypingEnvironments(newValue: GxOption[]) {
    if (newValue?.length) {
      this.prototypingEnvironmentsComboBoxModel =
        mapOptionsToComboBoxItemModel(newValue);
      this.#selectedPrototypingEnvironmentId = getSelectedItem(newValue);
      this.#updateFrontEnds();
    }
  }

  /**
   * Path to a directory where the information related to the KB will be stored/generated
   */
  @Prop() readonly prototypingTargets: GxOption[] = [];
  @Watch("prototypingTargets")
  watchPrototypingTargets(newValue: GxOption[]) {
    if (newValue?.length) {
      this.prototypingTargetsComboBoxModel =
        mapOptionsToComboBoxItemModel(newValue);
      this.prototypingTargetsInitialValue = getSelectedItem(newValue);
      this.#getPrototypingEnvironments();
    }
  }

  /**
   * Visible if something other than Windows Authentication is selected
   */
  @Prop() readonly savePassword: boolean = false;

  /**
   * This is a function provided by the developer that returns a string, with the location path
   */
  @Prop() readonly selectLocationCallback!: SelectLocationCallback;

  /**
   * Name of the DB server where we want to persist the information of our KB
   */
  @Prop() readonly serverNames: GxOption[] = [];
  @Watch("serverNames")
  watchServerNames(newValue: GxOption[]) {
    if (newValue?.length) {
      this.serverNamesComboBoxModel = mapOptionsToComboBoxItemModel(newValue);
      this.serverNamesInitialValue = getSelectedItem(newValue);
    }
  }

  /**
   * Specify the language in which the application screens will be developed (default language)
   */
  @Prop() readonly userInterfaceLanguages: GxOption[] = [];
  @Watch("userInterfaceLanguages")
  watchUserInterfaceLanguages(newValue: GxOption[]) {
    if (newValue?.length) {
      this.userInterfaceLanguagesComboBoxModel =
        mapOptionsToComboBoxItemModel(newValue);
      this.userInterfaceLanguagesInitialValue = getSelectedItem(newValue);
    }
  }

  /**
   * Username for the database connection. Visible if the Authentication Type is different from Windows Authentication (first item of the combo)
   */
  @Prop() readonly userName: string;

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);
    this.#kbSectionsModel = [
      {
        id: BASIC_TAB_SELECTOR,
        name: this.#componentLocale.tabBasic.tabLabel
      },
      {
        id: ADVANCED_TAB_SELECTOR,
        name: this.#componentLocale.tabAdvanced.tabLabel
      }
    ];

    this.#locationModeModel = [
      {
        value: LOCATION_MODE_DEFAULT,
        caption: this.#componentLocale.header.kbLocationDefault
      },
      {
        value: LOCATION_MODE_CUSTOM,
        caption: this.#componentLocale.header.kbLocationCustom
      }
    ];
  }

  connectedCallback() {
    this.watchDefaultDataSources(this.defaultDataSources);
    this.watchDefaultPrototypingEnvironments(
      this.defaultPrototypingEnvironments
    );
    this.watchPrototypingTargets(this.prototypingTargets);
    this.watchServerNames(this.serverNames);
    this.watchUserInterfaceLanguages(this.userInterfaceLanguages);
  }

  componentDidLoad() {
    this.#initializeValidatableControlsBasicTab();
  }

  componentDidRender() {
    if (this.advancedTabIsVisible && !this.#advancedTabHasBeenRendered) {
      // just call this once.
      this.#initialSetupAdvancedTab();
      this.#initializeValidatableControlsAdvancedTab();
      this.#advancedTabHasBeenRendered = true;
    }

    // Register the location input for validation once it's rendered (custom mode)
    if (
      this.locationMode === LOCATION_MODE_CUSTOM &&
      !this.#locationElRegistered &&
      this.#locationEl
    ) {
      this.validatableControls.set(this.#locationEl.id, {
        reference: this.#locationEl,
        hasError: false,
        message: undefined
      });
      this.#locationElRegistered = true;
    }
  }

  /**
   * Suspends or reactivates the shortcuts
   */
  @Method()
  async suspendShortcuts(suspendShortcuts: boolean) {
    if (suspendShortcuts) {
      this.#chShortcutsEl.suspend = true;
    } else {
      this.#chShortcutsEl.suspend = false;
    }
  }

  // Uncomment when "TODO: define button handler and logic" is done.
  // #geminiToolsSearchPrimary = getIconPath({
  //   category: "gemini-tools",
  //   name: "search",
  //   colorType: "primary"
  // });
  // #geminiToolsResetPrimary = getIconPath({
  //   category: "gemini-tools",
  //   name: "reset",
  //   colorType: "primary"
  // });

  #handlerAuthenticationTypeInput = (e: CustomEvent<string> | InputEvent) => {
    const authenticationTypeValue = e?.detail;
    this.#evaluateUserInputsDisplay(authenticationTypeValue as string);
  };

  #handlerServerNameInput = (e: CustomEvent<string> | InputEvent) => {
    this.#evaluateCreateDataCheckboxEnabled(e.detail as string);
  };

  #prototypingEnvironmentValueChangedHandler = (
    event: CustomEvent<string> | InputEvent
  ): void => {
    this.#selectedPrototypingEnvironmentId = event.detail as string;
    this.#getDataSources();
    this.#updateFrontEnds();
  };

  #prototypingTargetValueChangedHandler = async (): Promise<void> => {
    await this.#getPrototypingEnvironments();
    this.#getDataSources();
  };

  #dataSourceValueChangedHandler = () => {
    this.#selectedDataSourceId = this.#dataSourceEl.value;
  };

  #renderAdvanced = (): Element[] => {
    return [
      <h2
        // #knowledge base storage
        class="dialog-header subtitle-regular-xs	 text-align-center"
      >
        {this.#componentLocale.tabAdvanced.tabTitle}
      </h2>,
      <div class="field-group tab-advanced__field-group">
        <div
          // #server name
          class="field field-block server-name-field"
        >
          <label class="label" htmlFor="serverName">
            {this.#componentLocale.tabAdvanced.serverName.label}
          </label>
          <ch-combo-box-render
            class="combo-box"
            disabled={this.serverNames.length === 0}
            placeholder={
              this.serverNames.length === 0
                ? this.#componentLocale.tabAdvanced.serverName
                    .disabledPlaceholder
                : ""
            }
            value={this.serverNamesInitialValue}
            ref={(el: HTMLChComboBoxRenderElement) =>
              (this.#serverNameEl = el as HTMLChComboBoxRenderElement)
            }
            model={this.serverNamesComboBoxModel}
            onInput={
              this.serverNames.length > 0 && this.#handlerServerNameInput
            }
            id="serverName"
            part="server-name"
          ></ch-combo-box-render>
          {this.#evaluateTooltipRender(this.#serverNameEl)}
        </div>

        {/* <button
          TODO: define button handler and logic
          class="button-tertiary button-icon-only server-name-button"
          aria-label="caption"
          part="search-server-name"
          hidden
        >
          <ch-image
            class="icon-xs"
            src={this.#geminiToolsSearchPrimary}
          ></ch-image>
        </button> */}

        <div
          // #authentication type
          class="field field-block authentication-type-field"
        >
          <label class="label" htmlFor="authenticationType">
            {this.#componentLocale.tabAdvanced.authenticationType.label}
          </label>
          <ch-combo-box-render
            class="combo-box"
            disabled={this.authenticationTypes.length === 0}
            placeholder={
              this.authenticationTypes.length === 0
                ? this.#componentLocale.tabAdvanced.authenticationType
                    .disabledPlaceholder
                : ""
            }
            value={this.authenticationTypesInitialValue}
            ref={(el: HTMLChComboBoxRenderElement) =>
              (this.#authenticationTypeEl = el as HTMLChComboBoxRenderElement)
            }
            model={this.authenticationTypesComboBoxModel}
            id="authenticationType"
            part="authentication-type"
            onInput={
              this.authenticationTypes.length > 0
                ? this.#handlerAuthenticationTypeInput
                : undefined
            }
          ></ch-combo-box-render>
          {this.#evaluateTooltipRender(this.#authenticationTypeEl)}
        </div>

        <div
          // #database
          class="field field-block database-field"
        >
          <label class="label" htmlFor="databaseName">
            {this.#componentLocale.tabAdvanced.databaseName.label}
          </label>
          <ch-edit
            // TODO add placeholder
            class="input"
            value={this.databaseName}
            id="databaseName"
            part="database-name"
            ref={(el: HTMLChEditElement) =>
              (this.#databaseNameEl = el as HTMLChEditElement)
            }
          ></ch-edit>
          {this.#evaluateTooltipRender(this.#databaseNameEl)}
        </div>

        {this.userNameVisible ? (
          <div
            // #username
            class="field field-block username-field"
          >
            <label class="label" htmlFor="userName">
              {this.#componentLocale.tabAdvanced.userName.label}
            </label>
            <ch-edit
              // TODO add placeholder
              class="input"
              value={this.userName}
              id="userName"
              part="user-name"
              ref={(el: HTMLChEditElement) =>
                (this.#userNameEl = el as HTMLChEditElement)
              }
            ></ch-edit>
            {this.#evaluateTooltipRender(this.#userNameEl)}
          </div>
        ) : null}

        <div
          // #collation
          class="field field-block collation-field"
        >
          <label class="label" htmlFor="collation">
            {this.#componentLocale.tabAdvanced.collation.label}
          </label>
          <ch-combo-box-render
            class="combo-box"
            disabled={this.serverNames.length === 0}
            placeholder={
              this.collations.length === 0
                ? this.#componentLocale.tabAdvanced.collation
                    .disabledPlaceholder
                : ""
            }
            value={this.collationsInitialValue}
            ref={(el: HTMLChComboBoxRenderElement) =>
              (this.#collationEl = el as HTMLChComboBoxRenderElement)
            }
            model={this.collationsComboBoxModel}
            id="collation"
            part="collation"
          ></ch-combo-box-render>
          {this.#evaluateTooltipRender(this.#collationEl)}
        </div>

        {/* <button
          TODO: define button handler and logic 
          class="button-tertiary button-icon-only collation-button"
          aria-label="caption"
          part="reset-collation"
        >
          <ch-image
            class="icon-xs"
            src={this.#geminiToolsResetPrimary}
          ></ch-image>
        </button> */}

        {this.passwordVisible ? (
          <div
            // #password
            class="field field-block password-field"
          >
            <label class="label" htmlFor="password">
              {this.#componentLocale.tabAdvanced.password.label}
            </label>
            <ch-edit
              // TODO add placeholder
              class="input"
              type="password"
              value={this.password}
              id="password"
              part="password"
              ref={(el: HTMLChEditElement) =>
                (this.#passwordEl = el as HTMLChEditElement)
              }
            ></ch-edit>
            {this.#evaluateTooltipRender(this.#passwordEl)}
          </div>
        ) : null}

        <ch-checkbox
          // #create data in knowledge base folder
          class="checkbox create-data-in-kb-checkbox"
          checkedValue="createDataFilesInKBFolder"
          caption={this.#componentLocale.tabAdvanced.createDataFilesInKbFolder}
          disabled={this.createDataInKbDisabled}
          part="create-data-in-kb"
          id="createDataFilesInKBFolder"
          ref={(el: HTMLChCheckboxElement) =>
            (this.#createDataFilesInKBFolderEl = el as HTMLChCheckboxElement)
          }
        ></ch-checkbox>

        {this.savePasswordVisible ? (
          <ch-checkbox
            // #save password
            class="checkbox save-password-checkbox"
            checkedValue="password"
            caption={this.#componentLocale.tabAdvanced.savePassword}
            part="save-password"
            id="savePassword"
            ref={(el: HTMLChCheckboxElement) =>
              (this.#savePasswordEl = el as HTMLChCheckboxElement)
            }
          ></ch-checkbox>
        ) : null}
      </div>
    ];
  };

  #renderBasic = (): HTMLElement[] => {
    return [
      <section class="basic__header field-group dialog-header-with-border spacing-body-inline">
        <div
          // #prototyping  target
          class="field field-block prototyping-target"
        >
          <label class="label" htmlFor="prototypingTarget">
            {this.#componentLocale.tabBasic.prototypingTarget.label}
          </label>

          <ch-combo-box-render
            id="prototypingTarget"
            class="combo-box"
            part="prototyping-target"
            disabled={this.prototypingTargets.length === 0}
            placeholder={
              this.prototypingTargets.length === 0
                ? this.#componentLocale.tabBasic.prototypingTarget
                    .disabledPlaceholder
                : ""
            }
            value={this.prototypingTargetsInitialValue}
            model={this.prototypingTargetsComboBoxModel}
            onInput={
              this.prototypingTargets.length > 0
                ? this.#prototypingTargetValueChangedHandler
                : undefined
            }
            ref={(el: HTMLChComboBoxRenderElement) =>
              (this.#prototypingTargetEl = el as HTMLChComboBoxRenderElement)
            }
          ></ch-combo-box-render>
          {this.#evaluateTooltipRender(this.#prototypingTargetEl)}
        </div>

        <div
          // #user interface
          class="field field-block user-interface-language"
        >
          <label class="label" htmlFor="userInterfaceLanguage">
            {this.#componentLocale.tabBasic.userInterfaceLanguage.label}
          </label>
          <ch-combo-box-render
            class="combo-box"
            disabled={this.userInterfaceLanguages.length === 0}
            placeholder={
              this.userInterfaceLanguages.length === 0
                ? this.#componentLocale.tabBasic.userInterfaceLanguage
                    .disabledPlaceholder
                : ""
            }
            value={this.userInterfaceLanguagesInitialValue}
            ref={(el: HTMLChComboBoxRenderElement) =>
              (this.#userInterfaceLanguageEl =
                el as HTMLChComboBoxRenderElement)
            }
            model={this.userInterfaceLanguagesComboBoxModel}
            id="userInterfaceLanguage"
            part="user-interface-language"
          ></ch-combo-box-render>
          {this.#evaluateTooltipRender(this.#userInterfaceLanguageEl)}
        </div>
      </section>,

      <section
        class="section__back-end"
        // backend
      >
        <h2
          // back end
          class="dialog-header dialog-header-with-border dialog-header__back-end subtitle-regular-xs	 text-align-center spacing-body-inline"
        >
          {this.#componentLocale.tabBasic.backEnd.backEndLabel}
        </h2>
        <div class="field-group main__back-end spacing-body-inline scrollable">
          <div
            // #prototyping environment
            class="field field-block"
          >
            <label class="label" htmlFor="prototypingEnvironment">
              {
                this.#componentLocale.tabBasic.backEnd.prototypingEnvironment
                  .label
              }
            </label>
            <ch-combo-box-render
              class="combo-box"
              disabled={this.defaultPrototypingEnvironments.length === 0}
              placeholder={
                this.defaultPrototypingEnvironments.length === 0
                  ? this.#componentLocale.tabBasic.backEnd
                      .prototypingEnvironment.disabledPlaceholder
                  : ""
              }
              value={this.#selectedPrototypingEnvironmentId}
              ref={(el: HTMLChComboBoxRenderElement) =>
                (this.#prototypingEnvironmentEl =
                  el as HTMLChComboBoxRenderElement)
              }
              model={this.prototypingEnvironmentsComboBoxModel}
              onInput={this.#prototypingEnvironmentValueChangedHandler}
              part="prototyping-environment"
              id="prototypingEnvironment"
            ></ch-combo-box-render>
            {this.#evaluateTooltipRender(this.#prototypingEnvironmentEl)}
          </div>

          <div
            // #data source
            class="field field-block"
          >
            <label class="label" htmlFor="dataSource">
              {this.#componentLocale.tabBasic.backEnd.dataSource.label}
            </label>
            <ch-combo-box-render
              class="combo-box"
              disabled={this.defaultDataSources.length === 0}
              placeholder={
                this.defaultDataSources.length === 0
                  ? this.#componentLocale.tabBasic.backEnd.dataSource
                      .disabledPlaceholder
                  : ""
              }
              value={this.#selectedDataSourceId}
              ref={(el: HTMLChComboBoxRenderElement) =>
                (this.#dataSourceEl = el as HTMLChComboBoxRenderElement)
              }
              onInput={this.#dataSourceValueChangedHandler}
              model={this.defaultDataSourcesComboBoxModel}
              part="data-source"
              id="dataSource"
            ></ch-combo-box-render>
            {this.#evaluateTooltipRender(this.#dataSourceEl)}
          </div>
        </div>
      </section>,

      <section
        class="section__front-end"
        // front end
      >
        <h2 class="dialog-header dialog-header-with-border dialog-header__front-end subtitle-regular-xs	 text-align-center spacing-body-inline">
          {this.#componentLocale.tabBasic.frontEnd.frontEndLabel}
        </h2>
        <div class="field-group main__front-end spacing-body-inline scrollable">
          {renderChCheckboxItems(
            this.frontEnds,
            this.#frontEndCheckboxInputHandler,
            this.#frontEndsCheckedMap.get(
              this.#selectedPrototypingEnvironmentId
            )
          )}
        </div>
      </section>
    ];
  };

  // - - - - - - - - - -

  #cancelKbHandler = (): void => {
    if (this.cancelCallback) {
      this.cancelCallback();
    }
  };

  #createKbHandler = (): void => {
    const selectedFrontEndIds =
      this.#frontEndsCheckedMap.get(this.#selectedPrototypingEnvironmentId) ??
      [];

    this.createCallback({
      authenticationType: this.#authenticationTypeEl?.value,
      collation: this.#collationEl?.value,
      createDataFilesInKBFolder: !!this.#createDataFilesInKBFolderEl?.value,
      dataSource: this.#dataSourceEl?.value,
      databaseName: this.#databaseNameEl?.value,
      frontEnds: selectedFrontEndIds,
      kbLocation: this.#locationEl?.value,
      kbName: this.#kbNameEl?.value,
      password: this.#passwordEl?.value,
      prototypingEnvironment: this.#prototypingEnvironmentEl?.value,
      prototypingTarget: this.#prototypingTargetEl?.value,
      savePassword: !!this.#savePasswordEl?.value,
      serverName: this.#serverNameEl?.value,
      userInterfaceLanguage: this.#userInterfaceLanguageEl?.value,
      userName: this.#userNameEl?.value
    }).then((formSubmitResult: FormSubmitResult) => {
      this.validatableControls = validateControls(
        formSubmitResult,
        this.validatableControls
      );
    });
  };

  /**
   * Disable "Create Data in Knowledge Base Folder" if the first Server Name
   * option is selected.
   * */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #evaluateCreateDataCheckboxEnabled = (serverNameValue: string) => {
    if (this.serverNames[0]?.id === serverNameValue) {
      this.createDataInKbDisabled = true;
    } else {
      this.createDataInKbDisabled = false;
    }
  };

  // TODO: evaluate if this should be a global helper function.
  // It is used on new-kb as well.
  #evaluateTooltipRender = (
    controlReference: HTMLElement
  ): HTMLChTooltipElement =>
    this.validatableControls?.get(controlReference?.id)?.hasError &&
    this.validatableControls.get(controlReference.id)?.message && (
      <ch-tooltip
        class="tooltip"
        actionElement={controlReference as unknown as HTMLButtonElement}
        blockAlign={config.tooltipSettings.blockAlign}
        inlineAlign={config.tooltipSettings.inlineAlign}
        delay={config.tooltipSettings.delay}
      >
        {this.validatableControls.get(controlReference.id).message}
      </ch-tooltip>
    );

  /**
   * If "Authentication Type" is "Windows Authentication", hide User Name,
   * Password, and Save Password fields.
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #evaluateUserInputsDisplay = (authenticationTypeValue: string) => {
    if (authenticationTypeValue === WINDOWS_AUTHENTICATION_VALUE) {
      this.userNameVisible = false;
      this.passwordVisible = false;
      this.savePasswordVisible = false;
    } else {
      this.userNameVisible = true;
      this.passwordVisible = true;
      this.savePasswordVisible = true;
    }
  };

  #frontEndCheckboxInputHandler = (
    event: ChCheckboxCustomEvent<string> | InputEvent
  ) => {
    const checkedValue = (event.target as HTMLChCheckboxElement).checkedValue;
    const checked = event.detail === checkedValue;
    this.#updateFrontEndsCheckedMap(checkedValue, checked);
  };

  /**
   * This function updates frontEndsCheckedMap, that contains the checked
   * checkboxes for each language front ends.
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #updateFrontEndsCheckedMap = (checkboxId: string, checked: boolean) => {
    let currentState = this.#frontEndsCheckedMap.get(
      this.#selectedPrototypingEnvironmentId
    );
    if (!currentState) {
      // first time
      currentState = [];
      this.#frontEndsCheckedMap.set(
        this.#selectedPrototypingEnvironmentId,
        currentState
      );
    }
    if (checked) {
      // update by adding
      currentState.push(checkboxId);
    } else {
      // update by removing
      const checkboxIndex = currentState.findIndex(id => id === checkboxId);
      removeIndex(currentState, checkboxIndex);
    }
  };

  #getPrototypingEnvironments = async (): Promise<void> => {
    if (this.getPrototypingEnvironmentsCallback) {
      const selectedPrototypingTargetId = this.#prototypingTargetEl
        ? this.#prototypingTargetEl.value
        : this.prototypingTargetsInitialValue;
      const envs = await this.getPrototypingEnvironmentsCallback(
        selectedPrototypingTargetId
      );
      if (envs) {
        this.defaultPrototypingEnvironments = envs;
      }
    }
  };

  #getDataSources = (): void => {
    if (this.getDataSourcesCallback) {
      const selectedPrototypingEnvironmentId =
        this.#prototypingEnvironmentEl.value;
      const selectedPrototypingTargetId = this.#prototypingTargetEl.value;
      const updatedDefaultDataSourcesPromise: Promise<GxOption[]> =
        this.getDataSourcesCallback(
          selectedPrototypingEnvironmentId,
          selectedPrototypingTargetId
        );
      updatedDefaultDataSourcesPromise
        .then(result => {
          if (result.length > 0) {
            result = setSelectedItem(result, this.#selectedDataSourceId);
            this.#selectedDataSourceId = getSelectedItem(result);
            this.defaultDataSources = result;
            // set first option as selected
            this.#dataSourceEl.value =
              this.#selectedDataSourceId !== undefined
                ? this.#selectedDataSourceId
                : this.defaultDataSources[0].id;
          }
        })
        .catch(() => {
          // to do
        });
    }
  };

  #initialSetupAdvancedTab = () => {
    const selectedServerNameValue = this.#serverNameEl.value;
    this.#evaluateCreateDataCheckboxEnabled(selectedServerNameValue);

    const selectedAuthenticationType = this.#authenticationTypeEl.value;
    this.#evaluateUserInputsDisplay(selectedAuthenticationType);

    // map all "Advanced Tab" GxOption[]'s to ComboBoxModel's
    this.watchAuthenticationTypes(this.authenticationTypes);
    this.watchCollations(this.collations);
  };

  #initializeValidatableControlsBasicTab = () => {
    // populate #controlsValidation with the controls that could have errors.
    const validatableControls: HTMLElement[] = [
      this.#dataSourceEl,
      this.#kbNameEl,
      this.#prototypingEnvironmentEl,
      this.#prototypingTargetEl,
      this.#userInterfaceLanguageEl
    ];
    validatableControls.forEach(validatableControl => {
      if (validatableControl.id) {
        this.validatableControls.set(validatableControl.id, {
          reference: validatableControl,
          hasError: false,
          message: undefined
        });
      }
    });
  };

  #initializeValidatableControlsAdvancedTab = () => {
    // populate #controlsValidation with the controls that could have errors.
    const validatableControls: HTMLElement[] = [
      this.#authenticationTypeEl,
      this.#createDataFilesInKBFolderEl,
      this.#serverNameEl,
      this.#authenticationTypeEl,
      this.#databaseNameEl,
      this.#userNameEl,
      this.#collationEl,
      this.#passwordEl,
      this.#savePasswordEl
    ];
    validatableControls.forEach(validatableControl => {
      if (validatableControl.id) {
        this.validatableControls.set(validatableControl.id, {
          reference: validatableControl,
          hasError: false,
          message: undefined
        });
      }
    });
  };

  /**
   * @description It allows defining the DBMS that will be used in the solution
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #selectLocationHandler = (): void => {
    this.selectLocationCallback().then(kbLocation => {
      if (kbLocation) {
        this.kbLocation = kbLocation;
      } else {
        // to be defined
      }
    });
  };

  #locationModeChangeHandler = () => {
    this.locationMode = this.#locationModeEl.value;
  };

  #tabSelectionChangedHandler = (
    event: CustomEvent<{
      lastSelectedIndex: number;
      newSelectedId: string;
      newSelectedIndex: number;
    }>
  ): void => {
    if (event.detail.newSelectedId === ADVANCED_TAB_SELECTOR) {
      this.advancedTabIsVisible = true;
      // Some logic for advanced tab is done on componentDidRender lifecycle,
      // since the reference to the elements is needed.
    }
  };

  /**
   * Evaluates which front-end options need updates on 'getFrontEndsCallback'
   * by comparing the new state (getFrontEndsCallback result) and previous state.
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #updateFrontEnds = async () => {
    const updatedFrontEnds = await this.getFrontEndsCallback(
      this.#selectedPrototypingEnvironmentId
    );
    if (updatedFrontEnds?.length > 0) {
      this.frontEnds = updatedFrontEnds;

      // Seed the checked map from the data's checked property so that
      // initially checked items render as checked on first load.
      if (
        !this.#frontEndsCheckedMap.has(
          this.#selectedPrototypingEnvironmentId
        )
      ) {
        const checkedIds = updatedFrontEnds
          .filter(fe => fe.checked)
          .map(fe => fe.value || fe.id);

        if (checkedIds.length > 0) {
          this.#frontEndsCheckedMap.set(
            this.#selectedPrototypingEnvironmentId,
            checkedIds
          );
        }
      }
    }
  };

  render() {
    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <ch-shortcuts
          src={this.#shortcutsSrc}
          ref={(el: HTMLChShortcutsElement) =>
            (this.#chShortcutsEl = el as HTMLChShortcutsElement)
          }
        ></ch-shortcuts>
        <section class="section">
          <header
            class={{
              "field-group": true,
              "dialog-header-with-border": !this.isAdvanced,
              "dialog-header": this.isAdvanced,
              "header": true,
              "header-one-col": true,
              "spacing-body-block-start": true,
              "spacing-body-inline": true
            }}
          >
            <div
              // #kb name
              class="field field-inline kb-name"
            >
              <label class="label kb-label" htmlFor="kbName">
                {this.#componentLocale.header.kbName}
              </label>
              <ch-edit
                // TODO add placeholder
                autoFocus
                class="input kb-input"
                placeholder="SalesInventory"
                value={this.kbName}
                ref={(el: HTMLChEditElement) =>
                  (this.#kbNameEl = el as HTMLChEditElement)
                }
                id="kbName"
                part="kb-name"
              ></ch-edit>
              {this.#evaluateTooltipRender(this.#kbNameEl)}
            </div>

            {this.canSetLocation && (
              <div class="location-wrapper">
                <label class="label location-label">
                  {this.#componentLocale.header.kbLocation}
                </label>
                <ch-radio-group-render
                  class="radio-group"
                  model={this.#locationModeModel}
                  value={LOCATION_MODE_DEFAULT}
                  onChange={this.#locationModeChangeHandler}
                  ref={(el: HTMLChRadioGroupRenderElement) =>
                    (this.#locationModeEl =
                      el as HTMLChRadioGroupRenderElement)
                  }
                ></ch-radio-group-render>

                {this.locationMode === LOCATION_MODE_CUSTOM && (
                  <div class="location-custom-wrapper">
                    <div class="location-field">
                      <ch-edit
                        class="input"
                        value={this.kbLocation}
                        ref={(el: HTMLChEditElement) =>
                          (this.#locationEl = el as HTMLChEditElement)
                        }
                        id="kbLocation"
                        part="kb-location"
                      ></ch-edit>
                      {this.#evaluateTooltipRender(this.#locationEl)}
                    </div>
                    <button
                      class="button-tertiary button-icon-only location-icon"
                      aria-label={
                        this.#componentLocale.header.selectLocationButton
                      }
                      title={
                        this.#componentLocale.header.selectLocationButton
                      }
                      part="button-select-location"
                      onClick={this.#selectLocationHandler}
                    >
                      <ch-image
                        class="icon-m"
                        src={SYSTEM_ICON}
                      ></ch-image>
                    </button>
                  </div>
                )}
              </div>
            )}
          </header>

          {this.isAdvanced ? (
            <ch-tab-render
              // #tab
              class={{
                scrollable: true,
                tab: true,
                [MAIN_SECTION_CLASSES]: true
              }}
              tabListPosition="block-start"
              model={this.#kbSectionsModel}
              selectedId={BASIC_TAB_SELECTOR}
              overflow="auto"
              contain="size"
              onSelectedItemChange={
                !this.advancedTabIsVisible && this.#tabSelectionChangedHandler
              }
            >
              <div
                slot={BASIC_TAB_SELECTOR}
                class={BASIC_SECTION_PARENT_CLASSES}
                part="tab-button-basic"
              >
                {this.#renderBasic()}
              </div>

              {this.advancedTabIsVisible && (
                <div
                  slot={ADVANCED_TAB_SELECTOR}
                  class="advanced spacing-body-inline"
                  part="tab-button-advanced"
                >
                  {this.#renderAdvanced()}
                </div>
              )}
            </ch-tab-render>
          ) : (
            <div class={MAIN_SECTION_CLASSES}>
              <div class={BASIC_SECTION_PARENT_CLASSES}>
                {this.#renderBasic()}
              </div>
            </div>
          )}

          <footer
            class={{
              "dialog-footer": this.isAdvanced,
              "dialog-footer-with-border": !this.isAdvanced,
              "spacing-body-block-end": true,
              "spacing-body-inline": true
            }}
          >
            <div class="buttons-spacer">
              <button
                // #cancel
                class="button-secondary"
                id="button-cancel"
                onClick={this.#cancelKbHandler}
                part="button-cancel"
              >
                {this.#componentLocale.footer.btnCancel}
              </button>
              <button
                // #create
                class="button-primary"
                id="button-create"
                onClick={this.#createKbHandler}
                part="button-create"
              >
                {this.#componentLocale.footer.btnCreate}
              </button>
            </div>
          </footer>
        </section>
      </Host>
    );
  }
}
export interface NewKBData {
  authenticationType: string;
  collation: string;
  createDataFilesInKBFolder: boolean;
  databaseName: string;
  dataSource: string;
  frontEnds: string[];
  kbLocation: string;
  kbName: string;
  password: string;
  prototypingEnvironment: string;
  prototypingTarget: string;
  savePassword: boolean;
  serverName: string;
  userInterfaceLanguage: string;
  userName: string;
}

export type CreateCallback = (data: NewKBData) => Promise<FormSubmitResult>;

export type CancelCallback = () => Promise<void>;

export type GetDataSourcesCallback = (
  selectedPrototypingEnvironmentId: string,
  selectedPrototypingTargetId: string
) => Promise<GxOption[]>;

export type GetFrontEndsCallback = (
  selectedPrototypingEnvironmentId: string
) => Promise<GxOption[]>;

export type GetPrototypingEnvironmentsCallback = (
  selectedPrototypingTargetId: string
) => Promise<GxOption[]>;

export type SelectLocationCallback = () => Promise<string>;
